import clsx from 'clsx';
import { useQuery } from '@tanstack/react-query';
import { BarChart3, Bell, CalendarDays, LayoutGrid, Search, Settings, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ROUTES } from '@/app/router/routes';
import { moduleDashboardPath, resolveLocation, visibleModules } from '@/app/router/modules';
import { useAuth } from '@/features/auth/useAuth';
import { notificationsApi } from '@/services/api/endpoints/notifications';
import { queryKeys } from '@/services/api/queryKeys';
import { PERMISSIONS } from '@/types/permissions';

/**
 * The School OS sidebar: app-wide places at the top, the modules below.
 *
 * Deliberately not every page. A module's pages are one click away on its
 * dashboard, and a sidebar listing ninety entries is the problem the module
 * dashboards solve. Every page is still reachable by search (⌘K).
 *
 * Visibility follows permissions — a courtesy, never a control: each route is
 * guarded and the backend authorises every request.
 *
 * Two widths, one markup: 252px, or a 64px rail from 900px up (labels hidden
 * with a group variant, so icons never reflow). Below 900px it is a drawer.
 */
export function Sidebar({
  open,
  collapsed,
  onNavigate,
  onOpenSearch,
}: {
  /** Drawer state below 900px. */
  open: boolean;
  /** Rail state at 900px and above. */
  collapsed: boolean;
  onNavigate?: () => void;
  onOpenSearch: () => void;
}) {
  const { canAny, can, user } = useAuth();
  const { pathname } = useLocation();
  const modules = visibleModules(canAny);
  const currentModule = resolveLocation(pathname)?.module.key;

  const readsNotifications = can(PERMISSIONS.NOTIFICATION_SELF_VIEW);
  const unread = useQuery({
    queryKey: queryKeys.notifications.unreadCount,
    queryFn: notificationsApi.unreadCount,
    enabled: readsNotifications,
  });
  const unreadCount = unread.data?.unread ?? 0;
  const hasAdministration = modules.some((module) => module.key === 'administration');

  const initials = `${user?.firstName.charAt(0) ?? ''}${user?.lastName.charAt(0) ?? ''}`;
  const role = user?.roles[0]?.replaceAll('_', ' ').toLowerCase();

  return (
    <aside
      data-collapsed={collapsed ? '' : undefined}
      aria-label="Main navigation"
      className={clsx(
        'group fixed inset-y-0 left-0 z-40 flex shrink-0 flex-col bg-sidebar text-sidebar-ink',
        'w-[252px] transition-transform duration-150 ease-out',
        'min-[900px]:sticky min-[900px]:top-0 min-[900px]:h-screen min-[900px]:translate-x-0 min-[900px]:transition-[width]',
        collapsed ? 'min-[900px]:w-16' : 'min-[900px]:w-[252px]',
        open ? 'translate-x-0' : '-translate-x-full',
      )}
    >
      <div className="flex h-16 shrink-0 items-center gap-3 px-4">
        <div
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent text-[13px] font-bold text-accent-fg"
          aria-hidden="true"
        >
          OS
        </div>
        <div className="min-w-0 min-[900px]:group-data-[collapsed]:hidden">
          <div className="truncate text-[15px] font-semibold leading-tight tracking-[-0.01em] text-white">School OS</div>
          <div className="truncate text-[11.5px] leading-tight text-sidebar-muted">
            {user?.schoolId === undefined ? 'Platform' : 'School operations'}
          </div>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto px-2.5 pb-4 pt-2" aria-label="Sections">
        <ul className="space-y-0.5">
          <li>
            <SidebarLink to={ROUTES.dashboard} end icon={LayoutGrid} label="Dashboard" collapsed={collapsed} onNavigate={onNavigate} />
          </li>
          <li>
            <button
              type="button"
              onClick={onOpenSearch}
              title={collapsed ? 'Search' : undefined}
              className={ITEM_BASE + ' w-full text-sidebar-muted hover:bg-sidebar-hover hover:text-white'}
            >
              <Search className="h-[18px] w-[18px] shrink-0" aria-hidden="true" />
              <span className="flex-1 truncate text-left min-[900px]:group-data-[collapsed]:hidden">Search</span>
              <kbd className="rounded border border-sidebar-line px-1.5 text-[10.5px] text-sidebar-muted min-[900px]:group-data-[collapsed]:hidden">
                ⌘K
              </kbd>
            </button>
          </li>
          {readsNotifications && (
            <li>
              <SidebarLink
                to={ROUTES.notifications}
                icon={Bell}
                label="Notifications"
                collapsed={collapsed}
                onNavigate={onNavigate}
                trailing={
                  unreadCount > 0 ? (
                    <span className="rounded-full bg-accent px-1.5 text-[10.5px] font-semibold tabular-nums text-accent-fg">
                      <span className="sr-only">, </span>
                      {unreadCount > 99 ? '99+' : unreadCount}
                      <span className="sr-only"> unread</span>
                    </span>
                  ) : undefined
                }
              />
            </li>
          )}
          {can(PERMISSIONS.CALENDAR_VIEW) && (
            <li>
              <SidebarLink to={ROUTES.calendar} icon={CalendarDays} label="Calendar" collapsed={collapsed} onNavigate={onNavigate} />
            </li>
          )}
          {can(PERMISSIONS.DASHBOARD_VIEW) && (
            <li>
              <SidebarLink to={ROUTES.insights} icon={BarChart3} label="Reports" collapsed={collapsed} onNavigate={onNavigate} />
            </li>
          )}
          {hasAdministration && (
            <li>
              <SidebarLink
                to={moduleDashboardPath('administration')}
                icon={Settings}
                label="Settings"
                collapsed={collapsed}
                onNavigate={onNavigate}
              />
            </li>
          )}
        </ul>

        {modules.length > 0 && (
          <>
            <p className="mb-1.5 mt-6 px-3 text-[11px] font-medium uppercase tracking-[0.08em] text-sidebar-muted min-[900px]:group-data-[collapsed]:hidden">
              Modules
            </p>
            <ul className="space-y-0.5">
              {modules.map((module) => (
                <li key={module.key}>
                  <SidebarLink
                    to={moduleDashboardPath(module.key)}
                    icon={module.icon}
                    label={module.title}
                    collapsed={collapsed}
                    onNavigate={onNavigate}
                    forceActive={currentModule === module.key}
                  />
                </li>
              ))}
            </ul>
          </>
        )}
      </nav>

      <div className="shrink-0 border-t border-sidebar-line p-3">
        <div className="flex items-center gap-3 rounded-lg px-1 py-1">
          <span
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-sidebar-hover text-[11.5px] font-semibold text-white"
            aria-hidden="true"
          >
            {initials}
          </span>
          <div className="min-w-0 min-[900px]:group-data-[collapsed]:hidden">
            <p className="truncate text-[13px] font-medium text-white">{user?.fullName}</p>
            {role !== undefined && <p className="truncate text-[11.5px] capitalize text-sidebar-muted">{role}</p>}
          </div>
        </div>
      </div>
    </aside>
  );
}

const ITEM_BASE =
  'relative flex items-center gap-3 rounded-lg px-3 py-2 text-[13.5px] transition-colors duration-150 ' +
  'focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-[var(--sms-accent-ring)] ' +
  'min-[900px]:group-data-[collapsed]:justify-center min-[900px]:group-data-[collapsed]:px-0';

function SidebarLink({
  to,
  end = false,
  icon: IconComponent,
  label,
  collapsed,
  onNavigate,
  trailing,
  forceActive = false,
}: {
  to: string;
  end?: boolean;
  icon: LucideIcon | React.ComponentType<{ className?: string }>;
  label: string;
  collapsed: boolean;
  onNavigate?: () => void;
  trailing?: ReactNode;
  /** Active for every page inside the module, not only its dashboard. */
  forceActive?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      onClick={onNavigate}
      title={collapsed ? label : undefined}
      className={({ isActive }) =>
        clsx(
          ITEM_BASE,
          isActive || forceActive
            ? 'bg-sidebar-active font-medium text-white'
            : 'text-sidebar-muted hover:bg-sidebar-hover hover:text-white',
        )
      }
    >
      {({ isActive }) => (
        <>
          <span
            aria-hidden="true"
            className={clsx(
              'absolute inset-y-2 left-0 w-[3px] rounded-r-full bg-accent',
              isActive || forceActive ? 'opacity-100' : 'opacity-0',
            )}
          />
          <IconComponent className="h-[18px] w-[18px] shrink-0" aria-hidden="true" />
          <span className="flex-1 truncate min-[900px]:group-data-[collapsed]:hidden">{label}</span>
          {trailing !== undefined && <span className="min-[900px]:group-data-[collapsed]:hidden">{trailing}</span>}
        </>
      )}
    </NavLink>
  );
}
